import React, { FC, useState, useEffect, useRef, useCallback } from 'react';
import { eventCenter } from '@tarojs/taro';
import classNames from 'classnames';
import Popup from '../popup';
import { useDidShow, useDidHide } from '../../common/hooks';

interface INotifyProps {
  className?: string;
}

type INotifyType = 'info' | 'success' | 'error' | 'warning';

interface INotifyEventOptions {
  message?: string;
  type?: INotifyType;
  duration?: number;
}

const open = (option?: INotifyEventOptions) => {
  eventCenter.trigger('ink-notify', option);
};

const info = (option?: INotifyEventOptions) => {
  eventCenter.trigger('ink-notify', { ...option, type: 'info' });
};

const success = (option?: INotifyEventOptions) => {
  eventCenter.trigger('ink-notify', { ...option, type: 'success' });
};

const error = (option?: INotifyEventOptions) => {
  eventCenter.trigger('ink-notify', { ...option, type: 'error' });
};

const warning = (option?: INotifyEventOptions) => {
  eventCenter.trigger('ink-notify', { ...option, type: 'warning' });
};

const Notify: FC<INotifyProps> & { open: typeof open } & { info: typeof info } & { success: typeof success } & { error: typeof error } & { warning: typeof warning } = ({ className }) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState('');
  const [type, setType] = useState<INotifyType>('info');

  const rootClass = classNames('ink-notify', className, {
    [`ink-notify--${type}`]: true,
  });

  const cleanTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const bindMessageListener = useCallback(() => {
    eventCenter.on('ink-notify', ({ type: optType = 'info', duration: optDuration = 3000, message: optMessage = '' }: INotifyEventOptions = {}) => {
      setMessage(optMessage);
      setType(optType);
      setVisible(true);
      cleanTimer();
      timerRef.current = setTimeout(() => {
        setVisible(false);
      }, optDuration);
    });
  }, []);

  useDidShow(() => {
    bindMessageListener();
  });

  useDidHide(() => {
    eventCenter.off('ink-notify');
  });

  useEffect(() => {
    bindMessageListener();
    return () => {
      cleanTimer();
      eventCenter.off('ink-notify');
    };
  }, [bindMessageListener]);

  return (
    <Popup visible={visible} position="top" className={rootClass} maskClassName="ink-notify__mask">
      {message}
    </Popup>
  );
};

Notify.open = open;

Notify.info = info;

Notify.success = success;

Notify.error = error;

Notify.warning = warning;

export default Notify;
